import React from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  Paper,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";

import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupWorkIcon from "@mui/icons-material/GroupWork";

// Assets
import discoverImg from "../../components/assets/whoManages/DiscoverNewTalents.png";
import hostImg from "../../components/assets/whoManages/HostingEvents.png";
import collaborateImg from "../../components/assets/whoManages/CollaborateWithtalents.png";

/* ——— Netflix-ish tokens ——— */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
};

const RADIUS = 16;

const SectionRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  position: "relative",
  background: COLORS.bg,
  fontFamily: "Lexend, sans-serif",
  padding: theme.spacing(10, 2),
  overflow: "hidden",
  // subtle vignette & soft glow
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(800px 400px at 15% 10%, rgba(229,9,20,0.14), transparent 60%), radial-gradient(700px 420px at 85% 15%, rgba(255,255,255,0.06), transparent 60%)",
    pointerEvents: "none",
  },
}));

const MotionBox = motion(Box);

/* ——— Image tiles ——— */
const ImageCard = styled(Box)(({ theme }) => ({
  textAlign: "center",
}));

const ImageWrapper = styled(motion.div)(({ theme }) => ({
  width: "100%",
  maxWidth: 300,
  aspectRatio: "16/10",
  margin: "0 auto",
  borderRadius: RADIUS,
  overflow: "hidden",
  background: COLORS.panel,
  border: `1px solid ${COLORS.border}`,
  boxShadow: "0 12px 36px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04) inset",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const TileImage = styled("img")(() => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
}));

/* ——— Feature cards ——— */
const StyledCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: RADIUS,
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  backgroundColor: COLORS.panel,
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  boxShadow:
    "0 16px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset",
  transition: "border-color 180ms ease, transform 220ms ease",
  minHeight: 120,
  "&:hover": {
    borderColor: COLORS.red,
    transform: "translateY(-2px)",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  color: COLORS.text,
  display: "flex",
  alignItems: "center",
  // red accent dot behind icon
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "rgba(229,9,20,0.18)",
    filter: "blur(8px)",
    left: -4,
    top: -4,
    zIndex: 0,
  },
  "& .MuiSvgIcon-root": {
    position: "relative",
    zIndex: 1,
  },
}));

const WhoManagesSection: React.FC = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <ManageAccountsIcon fontSize="large" />,
      label: "Manage and promote your talents across diverse categories.",
    },
    {
      icon: <DashboardIcon fontSize="large" />,
      label: "Use our dashboard to monitor performance and fan engagement.",
    },
    {
      icon: <GroupWorkIcon fontSize="large" />,
      label:
        "Collaborate with talents, schedule showcases, or sponsor themed experiences.",
    },
  ];

  const imageTiles = [
    {
      src: discoverImg,
      alt: "Discover new talents",
      label: "Discover New Talents",
    },
    {
      src: hostImg,
      alt: "Host events and sessions",
      label: "Host Events & Schedules",
    },
    {
      src: collaborateImg,
      alt: "Collaborate with talents",
      label: "Collaborate With Talents",
    },
  ];

  return (
    <SectionRoot>
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 6 } }}>
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Heading */}
          <Typography
            variant="h4"
            fontWeight={900}
            mb={1}
            sx={{ color: COLORS.text, letterSpacing: "-0.02em" }}
          >
            WHO MANAGES
          </Typography>

          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ color: COLORS.textMuted }}
            mb={3}
          >
            For Platform Partners: Expand Your Reach, Manage Your Talents
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: COLORS.textMuted, maxWidth: 900, mb: 6 }}
          >
            StageLink is your infrastructure for real-time, talent-powered
            services.
          </Typography>

          {/* Image Tiles */}
          <Grid container spacing={3} justifyContent="center" mb={6}>
            {imageTiles.map((tile, idx) => (
              <Grid item xs={12} sm={4} key={idx}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                >
                  <ImageCard>
                    <ImageWrapper
                      whileHover={{ scale: 1.03 }}
                      transition={{ duration: 0.25 }}
                    >
                      <TileImage src={tile.src} alt={tile.alt} />
                    </ImageWrapper>
                    <Typography
                      variant="subtitle1"
                      fontWeight={800}
                      mt={2}
                      sx={{ color: COLORS.text }}
                    >
                      {tile.label}
                    </Typography>
                  </ImageCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Feature Cards */}
          <Grid container spacing={2.5}>
            {features.map((feature, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={index}
                sx={{ display: "flex" }}
              >
                <motion.div
                  style={{ display: "flex", width: "100%" }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <StyledCard>
                    <IconWrapper>{feature.icon}</IconWrapper>
                    <Typography
                      variant="body1"
                      sx={{ color: COLORS.textMuted, fontWeight: 600 }}
                    >
                      {feature.label}
                    </Typography>
                  </StyledCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* CTA / Closing Copy */}
          <Box mt={6} maxWidth={900}>
            <Typography variant="body1" sx={{ color: COLORS.textMuted }} paragraph>
              Entertainment agencies, MCNs, educational institutions, and content
              networks can expand their influence globally — without the technical
              headache.
            </Typography>
            <Typography
              variant="h6"
              fontWeight={900}
              sx={{ color: COLORS.text, letterSpacing: "-0.01em" }}
            >
              ✨ You run the platform.{" "}
              <Box component="span" sx={{ color: COLORS.red }}>
                We power the experience.
              </Box>
            </Typography>
          </Box>
        </MotionBox>
      </Container>
    </SectionRoot>
  );
};

export default WhoManagesSection;
